/**
 * Hero + HeroNote — the kicker / h1 / subtitle / supporting note block at the
 * top of every onboarding form column.
 *
 * Prototype's `.hero` and `.hero-note`. Kept generic so each screen passes its
 * own copy without a per-screen variant.
 */
import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@flint/ui";

export interface HeroProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Small uppercase eyebrow with a leading dot. Optional. */
  kicker?: React.ReactNode;
  /** Tone of the kicker dot. Default `sky`. */
  kickerTone?: "sky" | "ok" | "warn";
  /** The h1. */
  title: React.ReactNode;
  /** Subtitle paragraph. */
  description?: React.ReactNode;
}

export function Hero({
  kicker,
  kickerTone = "sky",
  title,
  description,
  className,
  children,
  ...props
}: HeroProps) {
  const dot = kickerTone === "ok" ? "bg-ok" : kickerTone === "warn" ? "bg-warn" : "bg-sky-600";
  return (
    <div className={cn("flex flex-col gap-3", className)} {...props}>
      {kicker ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted uppercase tracking-[0.08em]">
          <span aria-hidden className={cn("w-1.5 h-1.5 rounded-full", dot)} />
          {kicker}
        </span>
      ) : null}
      <h1 className="text-2xl leading-[32px] font-semibold tracking-[-0.01em] text-ink">
        {title}
      </h1>
      {description ? (
        <p className="text-sm leading-[22px] text-muted max-w-prose">{description}</p>
      ) : null}
      {children}
    </div>
  );
}

export interface HeroNoteProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Leading icon. Defaults to a check. */
  icon?: React.ReactNode;
}

export function HeroNote({ icon, className, children, ...props }: HeroNoteProps) {
  return (
    <div
      className={cn(
        "inline-flex items-start gap-2 text-xs leading-[17px] text-ink-2",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ok-50 text-ok"
      >
        {icon ?? <Check size={10} />}
      </span>
      <span>{children}</span>
    </div>
  );
}
